import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { uns } from "@/lib/uns";

/**
 * Editorial spotlight carousel — horizontal, drag-enabled, snap-based.
 * Signature moment: massive index numeral, italic caption, spotlight vignette.
 */

const shots = [
  {
    id: "chevy",
    label: "Chevrolet · Suburban Service",
    ...uns("photo-1583121274602-3e2820c69888", { w: 1400 }),
    tag: "Domestic",
  },
  {
    id: "nissan",
    label: "Nissan · Daily Driver Care",
    ...uns("photo-1487754180451-c456f719a1fc", { w: 1400 }),
    tag: "Import",
  },
  {
    id: "hyundai",
    label: "Hyundai · Precision Diagnostics",
    ...uns("photo-1486262715619-67b85e0b08d3", { w: 1400 }),
    tag: "Import",
  },
  {
    id: "bay",
    label: "Service Bay · Birmingham, AL",
    ...uns("photo-1503376780353-7e6692767b70", { w: 1400 }),
    tag: "Shop",
  },
  {
    id: "engine",
    label: "Under the Hood · Diagnostics",
    ...uns("photo-1552519507-da3b142c6e3d", { w: 1400 }),
    tag: "Precision",
  },
];

const ease = [0.22, 0.61, 0.36, 1];

export default function SpotlightGallery() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const total = shots.length;
  const frameRef = useRef(null);

  const goTo = useCallback((next) => {
    setDirection(next > index ? 1 : -1);
    setIndex(((next % total) + total) % total);
  }, [index, total]);

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  // Auto-advance every 5.5s. Pause on hover/focus/drag, when tab hidden, or reduced motion.
  useEffect(() => {
    if (paused) return;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const id = setInterval(() => {
      if (document.hidden) return;
      setDirection(1);
      setIndex((i) => (i + 1) % total);
    }, 5500);
    return () => clearInterval(id);
  }, [paused, total]);

  // Pause when tab is hidden
  useEffect(() => {
    const onVis = () => setPaused((p) => (document.hidden ? true : p));
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Keyboard nav when carousel is focused
  useEffect(() => {
    const onKey = (e) => {
      if (!frameRef.current) return;
      const rect = frameRef.current.getBoundingClientRect();
      const inView = rect.top < window.innerHeight * 0.7 && rect.bottom > window.innerHeight * 0.2;
      if (!inView) return;
      if (e.key === "ArrowRight") { next(); }
      if (e.key === "ArrowLeft") { prev(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const current = shots[index];

  const variants = {
    enter: (dir) => ({ x: dir > 0 ? "6%" : "-6%", opacity: 0, scale: 1.04 }),
    center: { x: "0%", opacity: 1, scale: 1 },
    exit: (dir) => ({ x: dir > 0 ? "-6%" : "6%", opacity: 0, scale: 1.02 }),
  };

  return (
    <section className="abc-spotlight" aria-label="Vehicles we service, photographed in shop">
      <div className="container">
        <header className="abc-spotlight-head abc-spotlight-head-row">
          <div>
            <span className="abc-mono-label">— On The Lift</span>
            <h2 className="abc-spotlight-title">
              Real vehicles.<br /><em>Real work.</em>
            </h2>
          </div>
          <div className="abc-carousel-controls" role="group" aria-label="Carousel controls">
            <button
              className="abc-carousel-btn"
              onClick={prev}
              aria-label="Previous photo"
              data-testid="spotlight-prev"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <span className="abc-carousel-counter" data-testid="spotlight-counter">
              <strong>{String(index + 1).padStart(2, "0")}</strong>
              <span>/{String(total).padStart(2, "0")}</span>
            </span>
            <button
              className="abc-carousel-btn"
              onClick={next}
              aria-label="Next photo"
              data-testid="spotlight-next"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </header>

        <div
          className="abc-carousel"
          ref={frameRef}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={(e) => {
            // Only unpause when focus leaves the carousel entirely
            if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
          }}
        >
          <div
            className="abc-carousel-frame"
            data-testid="spotlight-frame"
          >
            <motion.div
              className="abc-carousel-drag"
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.12}
              onDragStart={() => setPaused(true)}
              onDragEnd={(_, info) => {
                const t = 80;
                if (info.offset.x < -t) next();
                else if (info.offset.x > t) prev();
              }}
            >
              <AnimatePresence mode="wait" custom={direction} initial={false}>
                <motion.div
                  key={current.id}
                  className="abc-carousel-slide"
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.85, ease }}
                >
                  <img
                    src={current.src}
                    srcSet={current.srcSet}
                    sizes="(min-width: 900px) 1200px, 100vw"
                    alt={current.label}
                    draggable="false"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="abc-carousel-spot" aria-hidden="true" />
                  <span className="abc-carousel-big-num" aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </motion.div>
              </AnimatePresence>
            </motion.div>
            <div className={`abc-carousel-progress ${paused ? "is-paused" : ""}`} aria-hidden="true">
              <motion.span
                key={`${index}-${paused}`}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: paused ? undefined : 1 }}
                transition={{ duration: paused ? 0 : 5.5, ease: "linear" }}
              />
            </div>
          </div>

          <div className="abc-carousel-meta">
            <span className="abc-mono-label">{String(index + 1).padStart(2, "0")} / {current.tag}</span>
            <span className="abc-carousel-caption">{current.label}</span>
          </div>

          <ol className="abc-carousel-dots" role="tablist" aria-label="Choose a photo">
            {shots.map((s, i) => (
              <li key={s.id}>
                <button
                  type="button"
                  className={`abc-carousel-dot ${i === index ? "is-active" : ""}`}
                  onClick={() => goTo(i)}
                  aria-label={`Show ${s.label}`}
                  aria-selected={i === index}
                  role="tab"
                  data-testid={`spotlight-dot-${i}`}
                >
                  <span className="abc-carousel-dot-line" />
                </button>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
